import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
// import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';
import AccordionErrorClosurePracticeForm, { CancellationErrorPracticeVM } from './form';

interface ErrorClosureProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionErrorClosurePracticeComponent = ({
  loadUpdateDate,
}: ErrorClosureProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const params = useParams();

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/chiusura-pratica/annullo`, {
    options: { method: 'POST' },
  });

  const onSubmit = useEventCallback((values: CancellationErrorPracticeVM) => {
    const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
    const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
    const timestampChiusura =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      tsChiusura: timestampChiusura,
      motivoChiusura: values.closureReason || undefined,
    };

    (updateData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('La richiesta di chiusura per errore della pratica è stata registrata');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '9',
        //tipoOperazione: '06',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }
    });
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormik<CancellationErrorPracticeVM>({
    initialValues: {
      closureDate: DateTime.now(),
      closureTime: DateTime.now(),
      closureReason: '',
    },
    validationSchema: yup.object().shape({
      closureDate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closureTime: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closureReason: yup.string().nullable().required('Il campo è obbligatorio'),
    }),
    onSubmit,
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-error-closure-practice-content"
          id="panel-error-closure-practice-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Chiusura Pratica Errore</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionErrorClosurePracticeForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => formContext.handleSubmit()}
              id="confirm"
            >
              Conferma
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionErrorClosurePracticeComponent.displayName = 'Accordion Error Closure Practice component';

const AccordionErrorClosurenPractice = memo(AccordionErrorClosurePracticeComponent);

export default AccordionErrorClosurenPractice;
